// Deduction and formatting helpers for rental distribution.
//
// WHICH FEES DEDUCT FROM RENT — the rule, in one place:
//   fees_master WHERE lifecycle_stage = 'running_cost'
//                 AND the version is in force (valid_to IS NULL)
//
// The lifecycle stage is the whole rule. `running_cost` is defined as
// "recurring for as long as the property is held — settled periodically
// against rental income" (see FEE_LIFECYCLE_STAGE_DESCRIPTION), so every fee
// at that stage is a rent deduction regardless of how its value is expressed.
// Value basis and value type only affect HOW each line is calculated, never
// WHETHER it applies.
//
// The API applies the same rule server-side when it snapshots a distribution's
// fee lines. This module re-applies it only to preview the catalogue before a
// distribution exists — the snapshot, once written, is authoritative.

import type { BreakdownRow } from '@uiKit/components/BreakdownPopover.vue'
import { FEE_LIFECYCLE_STAGE } from '@shared/constants/fees'
import { formatFeeAmount, formatFeeCalculation } from '@shared/utils'
import type { FeeDefinition } from '@modules/settings'
import type {
  RentalDeductionLine,
  RentalPayoutDeductionLine,
} from '../types/rentalDistributionTypes'

export const RENTAL_DEDUCTION_LIFECYCLE_STAGE = FEE_LIFECYCLE_STAGE.RUNNING_COST

/**
 * Narrows the fee-master catalogue to the fees that deduct from rent.
 *
 * `getFeesMaster` already returns only the versions in force and can filter
 * `lifecycleStage` server-side, so this is a defensive re-application of the
 * same rule plus the active check — cheap, and it keeps the rule readable at
 * the one place that states it.
 *
 * Ordering is deterministic (code, then name) so the preview lists deductions
 * in the same order every time. `fees_master` has no ordering column, so this
 * is the ordering — and it is presentation only: because every fee is charged
 * against the gross collected rent independently rather than cascading, the
 * order lines appear in cannot change any amount.
 */
export function selectRentalDeductionFees(rows: FeeDefinition[]): FeeDefinition[] {
  return rows
    .filter((fee) => fee.lifecycleStage === RENTAL_DEDUCTION_LIFECYCLE_STAGE && fee.isActive)
    .sort((a, b) => a.code.localeCompare(b.code) || a.name.localeCompare(b.name))
}

/**
 * Distribution-level and payout-level lines both sort by their snapshot
 * sequence. Presentation only — see `selectRentalDeductionFees` on why order
 * is never load-bearing for an amount.
 */
export function sortBySequence<T extends { sequence: number }>(lines: T[]): T[] {
  return [...lines].sort((a, b) => a.sequence - b.sequence)
}

/**
 * Rows for a `BreakdownPopover` over a distribution's deduction lines — the
 * label says how the fee was arrived at, the value says what it cost.
 */
export function buildDeductionBreakdownRows(lines: RentalDeductionLine[]): BreakdownRow[] {
  return sortBySequence(lines).map((line) => ({
    label: `${line.feeName} — ${formatFeeCalculation({
      value: line.feeValue,
      valueType: line.valueType,
      valueBasis: line.valueBasis,
      unitBasis: line.unitBasis,
      unitQuantity: line.unitQuantity,
    })}`,
    value: formatFeeAmount(line.grossAmount),
  }))
}

/**
 * The same breakdown at investor level. The API pre-renders `rateLabel` here
 * (the payout line carries no calculation inputs of its own), so this reads it
 * rather than recomputing — one source of truth for the rate text.
 */
export function buildPayoutDeductionRows(lines: RentalPayoutDeductionLine[]): BreakdownRow[] {
  return sortBySequence(lines).map((line) => ({
    label: line.rateLabel ? `${line.feeName} — ${line.rateLabel}` : line.feeName,
    value: formatFeeAmount(line.grossAmount),
  }))
}

/**
 * Adds decimal-string amounts in integer cents, so 0.1 + 0.2 can never surface
 * as 0.30000000000000004 on a screen that credits wallets.
 */
export function sumAmounts(amounts: string[]): string {
  return fromCents(amounts.reduce((total, amount) => total + toCents(amount), 0))
}

/** The gross of every deduction line, summed the same way. */
export function sumDeductionGross(lines: Array<{ grossAmount: string }>): string {
  return sumAmounts(lines.map((line) => line.grossAmount))
}

function toCents(amount: string): number {
  return Math.round(Number(amount) * 100)
}

function fromCents(cents: number): string {
  return (cents / 100).toFixed(2)
}

const PERCENT_MULTIPLIER = 100

/**
 * `investor_rental_payouts.share_percentage` is a FRACTION in 0..1 — the DB
 * CHECKs `>= 0 AND <= 1`. Rendering it raw would show a one-third holder as
 * "0.33%", so it is multiplied here. This is the single place that conversion
 * happens.
 */
export function formatSharePercentage(share: string | null | undefined): string {
  if (share === null || share === undefined || share === '') return '—'
  const parsed = Number(share)
  if (Number.isNaN(parsed)) return '—'
  return `${(parsed * PERCENT_MULTIPLIER).toFixed(2)}%`
}

/** `'2026-09-01'` → `'2026-09'`. The DB stores dates, the UI groups by month. */
export function periodKeyFromDate(isoDate: string): string {
  return isoDate.slice(0, 7)
}

/** `'2026-09'` → `'September 2026'`; anything unparseable passes through. */
export function formatPeriodLabel(periodKey: string): string {
  const [year, month] = periodKey.split('-')
  const monthIndex = Number(month) - 1
  if (!year || !month || Number.isNaN(monthIndex) || monthIndex < 0 || monthIndex > 11) {
    return periodKey
  }
  const monthName = new Date(Date.UTC(2000, monthIndex, 1)).toLocaleString('en-US', {
    month: 'long',
    timeZone: 'UTC',
  })
  return `${monthName} ${year}`
}

/** Month-selector options, newest period first. */
export function buildPeriodOptions(periodKeys: string[]): Array<{ label: string; value: string }> {
  return [...periodKeys]
    .sort((a, b) => b.localeCompare(a))
    .map((periodKey) => ({ label: formatPeriodLabel(periodKey), value: periodKey }))
}
